import { useEffect, useMemo, useRef, useState } from "react";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';

import { ProjectList } from "../../common/interface";
import "./style.scss";


interface SearchableDropDownProps {
    projects: ProjectList[];
    selectedSubProject: ProjectList | null;
    handleSubProjectSelection: (project: ProjectList | null) => void;
    subProjectInputRef: React.RefObject<HTMLInputElement>
}

export const SearchableDropDown = ({ projects, selectedSubProject, handleSubProjectSelection, subProjectInputRef }: SearchableDropDownProps) => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [openDropdown, setOpenDropdown] = useState<boolean>(false);
    const dropDownRef = useRef<HTMLDivElement>(null);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value.trim());
        if (!openDropdown) setOpenDropdown(true);
    };

    const toggleDropdown = () => {
        setOpenDropdown((prev) => !prev);
    };

    const handleSelection = (subProjectOf: ProjectList | null = null) => {
        handleSubProjectSelection(subProjectOf);
        setOpenDropdown(false);
        setSearchTerm("");

        if (subProjectInputRef.current) {
            subProjectInputRef.current.value = "";
        }
        const subPorjectErrorElem = document.getElementById("subproject-error");
        if (subPorjectErrorElem) subPorjectErrorElem.remove();
    };

    const filterProjects = useMemo(() => {
        const lowerSearchTerm = searchTerm.toLowerCase();

        const recursiveFilter = (projects: ProjectList[]): ProjectList[] => {
            return projects.reduce((acc: ProjectList[], project: ProjectList) => {
                const isMatch = project.name?.toLowerCase().includes(lowerSearchTerm);
                const filteredProject = { ...project };

                if (isMatch) {
                    acc.push(filteredProject);
                } else if (filteredProject.subProjects?.length) {
                    const filteredChildren = recursiveFilter(filteredProject.subProjects);
                    if (filteredChildren.length) {
                        filteredProject.subProjects = filteredChildren;
                        acc.push(filteredProject);
                    }
                }
                return acc;
            }, []);
        };

        return recursiveFilter(projects);
    }, [searchTerm, projects]);

    const renderFilteredProjects = (filteredProjects: ProjectList[]) => {
        return filteredProjects.map((project: ProjectList) => (
            <div key={project.id}>
                <div
                    className="dropdown-item"
                    onClick={() => handleSelection(project)}
                >
                    {project.name}
                </div>
                {project.subProjects && project.subProjects.length > 0 && (
                    <div className="dropdown-submenu">
                        {renderFilteredProjects(project.subProjects)}
                    </div>
                )}
            </div>
        ));
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropDownRef.current && !dropDownRef.current.contains(event.target as Node)) {
                setOpenDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div ref={dropDownRef}>
            <div className="subproject-wrapper" onClick={toggleDropdown}>
                <input
                    className={`subproject-input ${selectedSubProject !== null ? "subproject-valid" : ""}`}
                    ref={subProjectInputRef}
                    placeholder={selectedSubProject !== null ? selectedSubProject.name : "Type to search..."}
                    onChange={handleSearch}
                />
                <div className="subproject-icon" >
                    {openDropdown ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </div>
                {(selectedSubProject !== null) && <div className="subproject-close-icon" onClick={(event) => { event.preventDefault(); handleSelection() }}>
                    <CloseIcon />
                </div>}
            </div>
            {
                openDropdown && (
                    <div className="subproject-dropdown">
                        {filterProjects.length > 0 && renderFilteredProjects(filterProjects)}
                    </div>
                )
            }
        </div >
    );
};